// Shared order helpers for the Backlog and Custom Plant tabs.
// Keep kicks math, CSV parsing and date helpers here so both tabs stay in sync.

// Gluers run every piece the die cutter made, so they get qty x outs
export const GLUERS = ['J&L 1', 'Turbox'];

// Case-insensitive so Custom Plant names read from the CSV (e.g. 'TURBOX') match too
export const isGluer = (machine) =>
  GLUERS.some(gluer => gluer.toUpperCase() === (machine || '').toUpperCase());

// Kicks for one order on one machine. The CSV's Qty. Ordered is already
// divided by outs, so die cutters use it as-is - never divide again.
// e.g. 30,000 @ 2 outs -> 30,000 kicks on the G7, 60,000 on the gluer.
export const getKicks = (order, machine) =>
  isGluer(machine) ? order.qtyOrdered * order.outs : order.qtyOrdered;

// Get the value to use for calculations based on unit mode
export const getOrderValue = (order, machine, unitMode) => {
  if (unitMode === 'kicks') {
    return getKicks(order, machine);
  }
  return order.area;
};

// Format number based on unit mode
export const formatValue = (num, unitMode) => {
  if (unitMode === 'kicks') {
    return num.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
  }
  return num.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

// The CSV can list the same OrderID on more than one row (e.g. 12000 @ 1 out
// and 6000 @ 2 outs). Use the row with the most outs, keeping its qty and
// outs together: 6000 @ 2 -> 6000 kicks on the die cutter, 12000 on J&L/Turbox.
export const mergeDuplicateOrders = (orders) => {
  const merged = new Map();
  orders.forEach(order => {
    const existing = merged.get(order.orderID);
    if (!existing) {
      merged.set(order.orderID, { ...order });
      return;
    }
    if (order.outs > existing.outs) {
      existing.qtyOrdered = order.qtyOrdered;
      existing.qtyOrderedStr = order.qtyOrderedStr;
      existing.outs = order.outs;
      existing.outsStr = order.outsStr;
    }
  });
  return [...merged.values()];
};

// Convert Excel serial date to JavaScript Date
export const excelDateToJSDate = (serial) => {
  // Excel's epoch is December 30, 1899
  // Excel serial date 1 = January 1, 1900
  const excelEpoch = new Date(1899, 11, 30); // December 30, 1899
  const daysOffset = serial;
  const jsDate = new Date(excelEpoch.getTime() + (daysOffset * 24 * 60 * 60 * 1000));
  jsDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone issues
  return jsDate;
};

// Helper function to parse dates from CSV (handles both M/D/YYYY format and Excel serial numbers)
export const parseDateFromCSV = (dateStr) => {
  if (!dateStr) return null;

  // Convert to string first
  const str = dateStr.toString().trim();

  // Check if it's an Excel serial number (5 digits, all numeric)
  if (/^\d{5}$/.test(str)) {
    const serial = parseInt(str);
    return excelDateToJSDate(serial);
  }

  // Try parsing as M/D/YYYY format
  const parts = str.split('/');
  if (parts.length === 3) {
    const month = parseInt(parts[0]) - 1; // JS months are 0-indexed
    const day = parseInt(parts[1]);
    const year = parseInt(parts[2]);
    const date = new Date(year, month, day);
    date.setHours(12, 0, 0, 0);
    return date;
  }

  // Fallback to standard Date parsing
  const date = new Date(str);
  if (!isNaN(date)) {
    date.setHours(12, 0, 0, 0);
    return date;
  }

  return null;
};

// Format date for comparison (YYYY-MM-DD)
export const formatDateForComparison = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date)) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Turn one CSV row into an order. Qty and outs are stored as numbers -
// kicks come from getKicks, never calculated here.
export const parseOrderRow = (row, idx) => {
  const routing = (row['Routing'] || '').toString().trim();
  const areaStr = (row['Area'] || '').toString().trim();
  const area = parseFloat(areaStr.replace(/,/g, '')) || 0;
  const dueDateStr = (row['Due Date'] || '').toString().trim();
  const dueDateParsed = parseDateFromCSV(dueDateStr);

  const qtyOrderedStr = (row['Qty. Ordered'] || '').toString().trim();
  const qtyOrdered = parseFloat(qtyOrderedStr.replace(/,/g, '')) || 0;
  const outsStr = (row['# Outs'] || '').toString().trim();
  const outs = parseFloat(outsStr.replace(/,/g, '')) || 1; // Default to 1 to avoid division by zero

  return {
    orderID: (row['OrderID'] || '').toString().trim(),
    customer: (row['Customer'] || '').toString().trim(),
    po: (row['PO'] || '').toString().trim(),
    dueDate: dueDateStr,
    dueDateParsed: dueDateParsed,
    dueDateComparison: formatDateForComparison(dueDateParsed),
    description: (row['Description'] || '').toString().trim(),
    routing: routing,
    area: area, // MSF value
    qtyOrdered: qtyOrdered,
    outs: outs,
    qtyOrderedStr: qtyOrderedStr, // Keep string version for display
    outsStr: outsStr, // Keep string version for display
    status: (row['Status'] || '').toString().trim(),
    specID: (row['SpecID'] || '').toString().trim(),
    grade: (row['Grade'] || '').toString().trim(),
    rowIndex: idx
  };
};

// Get next weekday (skip weekends)
export const getNextWeekday = (date) => {
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  next.setHours(12, 0, 0, 0);

  // If it's Saturday (6), skip to Monday
  if (next.getDay() === 6) {
    next.setDate(next.getDate() + 2);
  }
  // If it's Sunday (0), skip to Monday
  if (next.getDay() === 0) {
    next.setDate(next.getDate() + 1);
  }

  return next;
};

// Get previous weekday (skip weekends)
export const getPreviousWeekday = (date) => {
  const prev = new Date(date);
  prev.setDate(prev.getDate() - 1);
  prev.setHours(12, 0, 0, 0);

  // If it's Sunday (0), skip to Friday
  if (prev.getDay() === 0) {
    prev.setDate(prev.getDate() - 2);
  }
  // If it's Saturday (6), skip to Friday
  if (prev.getDay() === 6) {
    prev.setDate(prev.getDate() - 1);
  }

  return prev;
};

// Get the Monday of the week containing date
export const getMonday = (date) => {
  const d = new Date(date);
  d.setHours(12, 0, 0, 0);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(d.setDate(diff));
  monday.setHours(12, 0, 0, 0);
  return monday;
};

// Format date for display
export const formatDate = (date) => {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
